import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad from 'react-signature-canvas';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import { useSearchParams } from 'react-router';
import AiAwarenessSlide from '../components/AiAwarenessSlide';
import NavStatusBar from '../components/NavStatusBar';
import {
  AI_AWARENESS_TRAINING_COPY,
  AI_AWARENESS_TRAINING_QUESTIONS,
  AI_AWARENESS_TRAINING_SLIDES,
  AI_AWARENESS_TRAINING_STATUSES,
  type AiAwarenessAnswerOption,
  type AiAwarenessTrainingStatus,
} from '../config/aiAwarenessTrainingConfig';
import { getSignatureDataUrl, isSignatureValid } from '../utils/signatureValidation';
import { AI_AWARENESS_PASSING_ANSWER_COUNT } from '../../shared/trainingRequirements';

type QuestionFieldName = (typeof AI_AWARENESS_TRAINING_QUESTIONS)[number]['fieldName'];

interface QuestionFeedbackState {
  answer: AiAwarenessAnswerOption;
  correctAnswer: AiAwarenessAnswerOption;
  isCorrect: boolean;
  severity: 'success' | 'warning';
  text: string;
  action: 'advance';
  nextStatus: AiAwarenessTrainingStatus;
}

type StepMetadataShape = {
  questionFeedbackByStatus?: Partial<Record<AiAwarenessTrainingStatus, QuestionFeedbackState>>;
};

interface AiAwarenessTrainingRecord {
  id: number;
  nexusId: string;
  userNexusId: string | null;
  status: AiAwarenessTrainingStatus | string;
  completedAt: string | null;
  traineeName: string | null;
  question1Answer: AiAwarenessAnswerOption | '' | null;
  question2Answer: AiAwarenessAnswerOption | '' | null;
  question3Answer: AiAwarenessAnswerOption | '' | null;
  question4Answer: AiAwarenessAnswerOption | '' | null;
  question5Answer: AiAwarenessAnswerOption | '' | null;
  question6Answer: AiAwarenessAnswerOption | '' | null;
  bonusAnswer: AiAwarenessAnswerOption | '' | null;
  correctAnswerCount: number;
  score: number;
  hasSignature?: boolean;
  stepMetadata?: Record<string, unknown> | null;
}

interface SessionResponse {
  access: 'ok' | 'completed' | 'verification_required' | 'email_required';
  isExisting?: boolean;
  message?: string;
  emailHint?: string;
  user: { id: string; name: string };
  training?: AiAwarenessTrainingRecord;
}

interface VerifyEmailResponse {
  success: boolean;
  token?: string;
  user?: { id: string; name: string };
  verificationRequired?: boolean;
  challengeToken?: string;
  message?: string;
}

type MessageState = {
  severity: 'success' | 'info' | 'warning' | 'error';
  text: string;
} | null;

const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
const ACCESS_TOKEN_STORAGE_PREFIX = 'ai-awareness-training-access:';
const SCORED_QUESTION_COUNT = AI_AWARENESS_TRAINING_QUESTIONS.filter(
  (question) => question.status !== 'Bonus'
).length;

function normalizeStepMetadata(value: unknown): StepMetadataShape {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const raw = (value as StepMetadataShape).questionFeedbackByStatus;
  if (!raw || typeof raw !== 'object') return {};
  return { questionFeedbackByStatus: raw };
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || `Request failed (${response.status})`) as Error & {
      response?: { status: number; data: any };
    };
    error.response = { status: response.status, data };
    throw error;
  }
  return data as T;
}

async function apiGet<T>(url: string, options: { params?: Record<string, string>; headers?: HeadersInit } = {}): Promise<T> {
  const nextUrl = new URL(url, window.location.origin);
  Object.entries(options.params || {}).forEach(([key, value]) => nextUrl.searchParams.set(key, value));
  return parseJsonResponse<T>(await fetch(nextUrl.toString(), { method: 'GET', headers: options.headers }));
}

async function apiPost<T>(url: string, body: unknown, options: { headers?: HeadersInit } = {}): Promise<T> {
  return parseJsonResponse<T>(await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    body: JSON.stringify(body),
  }));
}

export default function AIAwarenessTraining() {
  const [searchParams] = useSearchParams();
  const userId = (searchParams.get('id') || '').trim();
  const isEmailMode = (searchParams.get('email') || searchParams.get('emailMode') || '').trim().toLowerCase() === 'true';
  const accessTokenStorageKey = userId ? `${ACCESS_TOKEN_STORAGE_PREFIX}${userId}` : null;

  const [session, setSession] = useState<AiAwarenessTrainingRecord | null>(null);
  const [accessState, setAccessState] = useState<'loading' | 'verification_required' | 'email_required' | 'ok' | 'invalid' | 'completed'>('loading');
  const [accessToken, setAccessToken] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationName, setVerificationName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailChallengeToken, setEmailChallengeToken] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [verificationEmailHint, setVerificationEmailHint] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [pageMessage, setPageMessage] = useState<MessageState>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloadingCertificate, setIsDownloadingCertificate] = useState(false);
  const [certificateError, setCertificateError] = useState('');
  const [completedViewMode, setCompletedViewMode] = useState<'existing' | 'fresh' | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<AiAwarenessAnswerOption | ''>('');
  const [answerError, setAnswerError] = useState('');
  const [questionFeedback, setQuestionFeedback] = useState<QuestionFeedbackState | null>(null);
  const [isAttested, setIsAttested] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [signatureRefreshKey, setSignatureRefreshKey] = useState(0);
  const [attestationError, setAttestationError] = useState('');
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const lastHydratedSignatureRef = useRef('');

  const currentStatus = useMemo<AiAwarenessTrainingStatus>(() => {
    const status = String(session?.status || AI_AWARENESS_TRAINING_STATUSES[0]);
    return AI_AWARENESS_TRAINING_STATUSES.find((value) => value === status) || AI_AWARENESS_TRAINING_STATUSES[0];
  }, [session?.status]);
  const currentStepIndex = Math.max(0, AI_AWARENESS_TRAINING_STATUSES.indexOf(currentStatus));
  const currentSlide = useMemo(
    () => AI_AWARENESS_TRAINING_SLIDES.find((slide) => slide.status === currentStatus) || null,
    [currentStatus]
  );
  const currentQuestion = useMemo(
    () => AI_AWARENESS_TRAINING_QUESTIONS.find((question) => question.status === currentStatus) || null,
    [currentStatus]
  );
  const persistedFeedback = useMemo(() => {
    if (!currentQuestion || !session) return null;
    return normalizeStepMetadata(session.stepMetadata).questionFeedbackByStatus?.[currentQuestion.status] || null;
  }, [currentQuestion, session]);
  const previousStatus = currentStepIndex > 0
    ? AI_AWARENESS_TRAINING_STATUSES[currentStepIndex - 1] as AiAwarenessTrainingStatus
    : null;

  const furthestAccessibleStepIndex = useMemo(() => {
    if (!session) return currentStepIndex;
    let furthest = currentStepIndex;
    const feedbackByStatus = normalizeStepMetadata(session.stepMetadata).questionFeedbackByStatus || {};
    Object.values(feedbackByStatus).forEach((feedback) => {
      if (feedback?.nextStatus) {
        furthest = Math.max(furthest, AI_AWARENESS_TRAINING_STATUSES.indexOf(feedback.nextStatus));
      }
    });
    if (session.correctAnswerCount >= AI_AWARENESS_PASSING_ANSWER_COUNT && AI_AWARENESS_TRAINING_QUESTIONS.every((question) => Boolean(String(session[question.fieldName] || '').trim()))) {
      furthest = Math.max(furthest, AI_AWARENESS_TRAINING_STATUSES.indexOf('Attestation'));
    }
    return furthest;
  }, [currentStepIndex, session]);

  const statusBarOptions = useMemo(() => AI_AWARENESS_TRAINING_STATUSES.map((status, index) => ({
    value: status,
    label: status,
    disabled: status === 'Completed' || index > furthestAccessibleStepIndex
      || (status === 'Attestation' && (session?.correctAnswerCount ?? 0) < AI_AWARENESS_PASSING_ANSWER_COUNT),
  })), [furthestAccessibleStepIndex, session?.correctAnswerCount]);

  const loadSession = useCallback(async (tokenOverride?: string) => {
    const nextAccessToken = tokenOverride ?? (accessTokenStorageKey ? sessionStorage.getItem(accessTokenStorageKey) ?? '' : '');
    try {
      const responseData = await apiGet<SessionResponse>(`${API_BASE_URL}/ai-awareness-training/session`, {
        params: userId ? { id: userId } : { emailMode: 'true' },
        headers: nextAccessToken ? { Authorization: `Bearer ${nextAccessToken}` } : undefined,
      });
      setVerificationEmailHint(responseData.emailHint || '');
      if (responseData.access === 'verification_required' || responseData.access === 'email_required') {
        setAccessToken('');
        if (accessTokenStorageKey) sessionStorage.removeItem(accessTokenStorageKey);
        setSession(null);
        setAccessState(responseData.access);
        setPageMessage(null);
        return;
      }
      if (nextAccessToken) setAccessToken(nextAccessToken);
      if (!responseData.training) {
        setAccessState('invalid');
        setPageMessage({ severity: 'error', text: 'Unable to load this training session.' });
        return;
      }
      setVerificationError('');
      setSession(responseData.training);
      setSignatureData('');
      if (responseData.access === 'completed') {
        setAccessState('completed');
        setCompletedViewMode('existing');
        setPageMessage({ severity: 'info', text: responseData.message || 'This AI awareness training has already been completed.' });
      } else {
        setAccessState('ok');
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || error?.message || 'This training link is not valid.';
      if (status === 401) {
        const requestedAccessState = error?.response?.data?.access;
        setAccessToken('');
        if (accessTokenStorageKey) sessionStorage.removeItem(accessTokenStorageKey);
        setSession(null);
        setAccessState(requestedAccessState === 'email_required' || requestedAccessState === 'verification_required'
          ? requestedAccessState
          : isEmailMode ? 'email_required' : 'verification_required');
        setVerificationError(requestedAccessState ? '' : message);
        return;
      }
      setAccessState('invalid');
      setPageMessage({ severity: 'error', text: message });
    }
  }, [accessTokenStorageKey, isEmailMode, userId]);

  useEffect(() => {
    if (!userId && !isEmailMode) {
      setAccessState('invalid');
      setPageMessage({ severity: 'error', text: 'This training link is not valid.' });
      setIsInitialLoading(false);
      return;
    }
    let mounted = true;
    void loadSession().finally(() => { if (mounted) setIsInitialLoading(false); });
    return () => { mounted = false; };
  }, [isEmailMode, loadSession, userId]);

  useEffect(() => {
    if (!currentQuestion || !session) {
      setSelectedAnswer('');
      setQuestionFeedback(null);
      return;
    }
    setSelectedAnswer((persistedFeedback?.answer || session[currentQuestion.fieldName] || '') as AiAwarenessAnswerOption | '');
    setAnswerError('');
    setQuestionFeedback(persistedFeedback);
  }, [currentQuestion, persistedFeedback, session]);

  useEffect(() => {
    if (currentStatus !== 'Attestation') {
      setIsAttested(false);
      setAttestationError('');
      lastHydratedSignatureRef.current = '';
      return;
    }
    if (!signaturePadRef.current) return;
    const signatureToRender = signatureData.trim();
    if (signatureToRender && lastHydratedSignatureRef.current !== signatureToRender) {
      try {
        signaturePadRef.current.clear();
        signaturePadRef.current.fromDataURL(signatureToRender);
        lastHydratedSignatureRef.current = signatureToRender;
      } catch {
        // Users can sign again if a stored signature cannot be rendered.
      }
    }
  }, [currentStatus, signatureData, signatureRefreshKey]);

  const handleVerifyEmail = async () => {
    const normalizedEmail = verificationEmail.trim();
    const normalizedName = verificationName.replace(/\s+/g, ' ').trim();
    if (isEmailMode && normalizedName.length < 2) {
      setVerificationError('Enter your full name for the training certificate.');
      return;
    }
    if (!normalizedEmail) {
      setVerificationError('Enter the email address on your account to continue.');
      return;
    }
    if (isEmailMode && emailChallengeToken && !/^\d{6}$/.test(verificationCode.trim())) {
      setVerificationError('Enter the 6-digit verification code from your email.');
      return;
    }
    setIsVerifyingEmail(true);
    setVerificationError('');
    try {
      const responseData = await apiPost<VerifyEmailResponse>(`${API_BASE_URL}/ai-awareness-training/verify-email`, isEmailMode
        ? {
            email: normalizedEmail,
            traineeName: normalizedName,
            emailMode: true,
            ...(emailChallengeToken ? {
              verificationCode: verificationCode.trim(),
              challengeToken: emailChallengeToken,
            } : {}),
          }
        : { id: userId, email: normalizedEmail });
      if (responseData.verificationRequired && responseData.challengeToken) {
        setEmailChallengeToken(responseData.challengeToken);
        setVerificationCode('');
        setVerificationMessage(responseData.message || 'A verification code was sent to your email address.');
        return;
      }
      if (!responseData.token) throw new Error('Unable to verify your email right now.');
      if (accessTokenStorageKey) sessionStorage.setItem(accessTokenStorageKey, responseData.token);
      setAccessToken(responseData.token);
      await loadSession(responseData.token);
    } catch (error: any) {
      setVerificationError(error?.response?.data?.message || error?.message || 'Unable to verify your email right now.');
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const saveProgress = useCallback(async (updates: Record<string, unknown>) => {
    if (!session) return null;
    setIsSaving(true);
    try {
      const responseData = await apiPost<{ success: boolean; training: AiAwarenessTrainingRecord }>(
        `${API_BASE_URL}/ai-awareness-training/progress`,
        { trainingId: session.id, id: userId, emailMode: isEmailMode, formData: updates },
        { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined }
      );
      setSession(responseData.training);
      return responseData.training;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setAccessToken('');
        if (accessTokenStorageKey) sessionStorage.removeItem(accessTokenStorageKey);
        setSession(null);
        setAccessState(isEmailMode ? 'email_required' : 'verification_required');
        setVerificationError(error?.response?.data?.message || 'Please enter your email address to continue.');
      }
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [accessToken, accessTokenStorageKey, isEmailMode, session, userId]);

  const handleStatusTransition = useCallback(async (nextStatus: AiAwarenessTrainingStatus) => {
    try {
      await saveProgress({ status: nextStatus });
      setPageMessage(null);
      setAnswerError('');
      setQuestionFeedback(null);
    } catch (error: any) {
      setPageMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Unable to save your training progress right now.' });
    }
  }, [saveProgress]);

  const handleQuestionSubmit = async () => {
    if (!currentQuestion || !selectedAnswer) {
      setAnswerError('Select an answer before continuing.');
      return;
    }
    try {
      const updated = await saveProgress({ questionSubmission: { status: currentQuestion.status, answer: selectedAnswer } });
      const feedback = normalizeStepMetadata(updated?.stepMetadata).questionFeedbackByStatus?.[currentQuestion.status] || null;
      setQuestionFeedback(feedback);
      setAnswerError('');
    } catch (error: any) {
      setAnswerError(error?.response?.data?.message || error?.message || 'Unable to save this answer.');
    }
  };

  const handleAttestationSubmit = async () => {
    if (!isAttested) {
      setAttestationError('Confirm the acknowledgment before finishing.');
      return;
    }
    const currentSignatureData = getSignatureDataUrl(signaturePadRef.current, signatureData);

    if (!isSignatureValid(signaturePadRef.current, currentSignatureData)) {
      setAttestationError('Add your signature before finishing.');
      return;
    }
    try {
      setSignatureData(currentSignatureData);
      const updated = await saveProgress({ completeAttestation: true, signature: currentSignatureData });
      if (!updated || updated.status !== 'Completed' || updated.correctAnswerCount < AI_AWARENESS_PASSING_ANSWER_COUNT) {
        throw new Error('The training has not been completed. Please retry the knowledge check.');
      }
      setSession(updated);
      setCompletedViewMode('fresh');
      setAccessState('completed');
      setPageMessage({ severity: 'success', text: 'AI awareness training completed successfully.' });
    } catch (error: any) {
      setAttestationError(error?.response?.data?.message || error?.message || 'Unable to finish the training right now.');
    }
  };

  const handleKnowledgeCheckRetry = async () => {
    try {
      await saveProgress({ restartTraining: true });
      setAccessState('ok');
      setCompletedViewMode(null);
      setPageMessage(null);
      setQuestionFeedback(null);
      setSelectedAnswer('');
      setAnswerError('');
      setIsAttested(false);
      setSignatureData('');
      lastHydratedSignatureRef.current = '';
      setSignatureRefreshKey((value) => value + 1);
      setAttestationError('');
    } catch (error: any) {
      setPageMessage({ severity: 'error', text: error?.message || 'Unable to restart the knowledge check.' });
    }
  };

  const handleCertificateDownload = async () => {
    if (!session) return;
    setIsDownloadingCertificate(true);
    setCertificateError('');
    try {
      const response = await fetch(`${API_BASE_URL}/ai-awareness-training/certificate/${session.id}`, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || 'Unable to download the training certificate.');
      }
      const disposition = response.headers.get('Content-Disposition') || '';
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || 'AI-Awareness-Training-Certificate.pdf';
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setCertificateError(error?.message || 'Unable to download the training certificate.');
    } finally {
      setIsDownloadingCertificate(false);
    }
  };

  const renderVerification = () => (
    <Container maxWidth="sm" sx={{ mt: 6, mb: 6 }}>
      <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
        <Stack component="form" spacing={3} onSubmit={(event) => { event.preventDefault(); void handleVerifyEmail(); }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {isEmailMode && emailChallengeToken ? 'Check Your Email' : isEmailMode ? 'Enter Your Email' : 'Verify Your Email'}
            </Typography>
            <Typography color="text.secondary">
              {isEmailMode && emailChallengeToken
                ? 'Enter the 6-digit code we sent to your email address.'
                : isEmailMode
                ? 'Enter your email address to start or resume this AI awareness training.'
                : 'Enter the email address on your account to continue this AI awareness training.'}
            </Typography>
            {verificationEmailHint ? <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Expected email: {verificationEmailHint}</Typography> : null}
          </Box>
          {isEmailMode ? (
            <TextField
              label="Full name"
              value={verificationName}
              onChange={(event) => {
                setVerificationName(event.target.value);
                setVerificationError('');
                setVerificationMessage('');
                setVerificationCode('');
                setEmailChallengeToken('');
              }}
              fullWidth
              required
              autoComplete="name"
              autoFocus
              inputProps={{ maxLength: 120 }}
              helperText="This name will appear on your certificate of completion."
            />
          ) : null}
          <TextField
            label="Email address"
            type="email"
            value={verificationEmail}
            onChange={(event) => {
              setVerificationEmail(event.target.value);
              setVerificationError('');
              setVerificationMessage('');
              setVerificationCode('');
              setEmailChallengeToken('');
            }}
            fullWidth
            autoComplete="email"
            autoFocus={!isEmailMode}
          />
          {isEmailMode && emailChallengeToken ? (
            <TextField
              label="Verification code"
              value={verificationCode}
              onChange={(event) => {
                setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                setVerificationError('');
              }}
              fullWidth
              required
              autoFocus
              autoComplete="one-time-code"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 }}
            />
          ) : null}
          {verificationMessage ? <Alert severity="info">{verificationMessage}</Alert> : null}
          {verificationError ? <Alert severity="error">{verificationError}</Alert> : null}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            {isEmailMode && emailChallengeToken ? (
              <Button
                type="button"
                onClick={() => {
                  setEmailChallengeToken('');
                  setVerificationCode('');
                  setVerificationMessage('');
                  setVerificationError('');
                }}
              >
                Send a new code
              </Button>
            ) : <span />}
            <Button variant="contained" type="submit" disabled={isVerifyingEmail}>
              {isVerifyingEmail
                ? (isEmailMode ? (emailChallengeToken ? 'Verifying...' : 'Sending...') : 'Verifying...')
                : (isEmailMode ? (emailChallengeToken ? 'Verify Code' : 'Send Code') : 'Verify Email')}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );

  if (isInitialLoading) return <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}><CircularProgress color="inherit" /></Backdrop>;
  if (accessState === 'verification_required' || accessState === 'email_required') return renderVerification();
  if (accessState === 'invalid' || !session) {
    return <Container maxWidth="sm" sx={{ mt: 6, mb: 6 }}><Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', borderRadius: 3 }}><CancelOutlinedIcon sx={{ fontSize: 64, color: '#8900e1', mb: 2 }} /><Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Unable to Access Training</Typography><Typography color="text.secondary">{pageMessage?.text || 'This training link is not valid.'}</Typography></Paper></Container>;
  }
  const hasFailedKnowledgeCheck = (session.correctAnswerCount < AI_AWARENESS_PASSING_ANSWER_COUNT &&
    AI_AWARENESS_TRAINING_QUESTIONS.filter((question) => question.status !== 'Bonus')
      .every((question) => Boolean(session[question.fieldName]))
  ) || ((currentStatus === 'Attestation' || currentStatus === 'Completed') && (
    session.correctAnswerCount < AI_AWARENESS_PASSING_ANSWER_COUNT
    || !AI_AWARENESS_TRAINING_QUESTIONS.every((question) => Boolean(session[question.fieldName]))
  ));
  if (hasFailedKnowledgeCheck) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, mb: 6 }}>
        <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <QuizOutlinedIcon sx={{ fontSize: 64, color: '#8900e1' }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Knowledge Check Not Passed</Typography>
            <Typography>You scored {session.correctAnswerCount} out of {SCORED_QUESTION_COUNT}. At least {AI_AWARENESS_PASSING_ANSWER_COUNT} correct answers are required. Please retry the knowledge check to complete your training and receive a certificate.</Typography>
            {pageMessage?.severity === 'error' ? <Alert severity="error">{pageMessage.text}</Alert> : null}
            <Button variant="contained" disabled={isSaving} onClick={() => void handleKnowledgeCheckRetry()}>
              {isSaving ? 'Restarting...' : 'Retry Knowledge Check'}
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }
  if (accessState === 'completed') {
    return <Container maxWidth="md" sx={{ mt: 5, mb: 6 }}><Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}><Stack spacing={3} alignItems="center" textAlign="center"><CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#8900e1' }} /><Typography variant="h4" sx={{ fontWeight: 700 }}>{completedViewMode === 'existing' ? 'Training Already Completed' : AI_AWARENESS_TRAINING_COPY.completed.title}</Typography><Typography color="text.secondary">{completedViewMode === 'existing' ? 'This AI awareness training has already been completed.' : AI_AWARENESS_TRAINING_COPY.completed.body}</Typography>{session.completedAt ? <Typography variant="body2" color="text.secondary">Completed at {formatDateTime(session.completedAt)}</Typography> : null}{certificateError ? <Alert severity="error" sx={{ width: '100%', maxWidth: 560 }}>{certificateError}</Alert> : null}<Button variant="contained" startIcon={<DownloadOutlinedIcon />} onClick={() => void handleCertificateDownload()} disabled={isDownloadingCertificate}>{isDownloadingCertificate ? 'Preparing Certificate...' : 'Download Certificate'}</Button></Stack></Paper></Container>;
  }

  const isQuestionLocked = Boolean(questionFeedback);
  const displayedAnswer = questionFeedback?.answer || selectedAnswer;
  const correctOption = currentQuestion && questionFeedback
    ? currentQuestion.options.find((option) => option.value === questionFeedback.correctAnswer)
    : null;

  return (
    <Container maxWidth={false} disableGutters sx={{ mt: { xs: 2, md: 2 }, mb: 4 }}>
      <Backdrop open={isSaving} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}><CircularProgress color="inherit" /></Backdrop>
      <Paper elevation={6} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3, width: '100%', maxWidth: 1440, mx: 'auto' }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>AI Awareness Training</Typography>
          </Box>
          <NavStatusBar condensed steps={statusBarOptions.map((option) => ({ status: option.value, title: option.label, disabled: option.disabled }))} currentStep={currentStepIndex} onStepClick={(index) => { const option = statusBarOptions[index]; if (option && !option.disabled && option.value !== currentStatus) void handleStatusTransition(option.value); }} disabled={isSaving} />
          {pageMessage ? <Alert severity={pageMessage.severity}>{pageMessage.text}</Alert> : null}

          {currentSlide ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', containerType: 'inline-size', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', backgroundColor: '#07051a' }}>
                <AiAwarenessSlide status={currentSlide.status} />
              </Box>
              <Typography variant="body2" color="text.secondary" textAlign="center">Training slide {currentStepIndex + 1} of {AI_AWARENESS_TRAINING_SLIDES.length}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button variant="outlined" disabled={!previousStatus} onClick={() => previousStatus && void handleStatusTransition(previousStatus)}>Back</Button>
                <Button variant="contained" onClick={() => void handleStatusTransition(AI_AWARENESS_TRAINING_STATUSES[currentStepIndex + 1])}>Next</Button>
              </Box>
            </>
          ) : null}

          {currentStatus === 'Knowledge Check' ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center"><QuizOutlinedIcon color="action" /><Typography variant="h4" sx={{ fontWeight: 700 }}>{AI_AWARENESS_TRAINING_COPY.knowledgeCheck.title}</Typography></Stack>
              <Typography>{AI_AWARENESS_TRAINING_COPY.knowledgeCheck.body}</Typography>
              <Alert severity="info">{AI_AWARENESS_TRAINING_COPY.knowledgeCheck.note}</Alert>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Button variant="outlined" onClick={() => void handleStatusTransition('Key Takeaways')}>Back</Button><Button variant="contained" onClick={() => void handleStatusTransition('Question 1')}>Start Questions</Button></Box>
            </>
          ) : null}

          {currentQuestion ? (
            <>
              <FormControl fullWidth error={Boolean(answerError)} disabled={isQuestionLocked}>
                <FormLabel sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>{currentQuestion.prompt}</FormLabel>
                <RadioGroup value={displayedAnswer} onChange={(event) => { setSelectedAnswer(event.target.value as AiAwarenessAnswerOption); setAnswerError(''); }}>
                  {currentQuestion.options.map((option) => {
                    const isSelected = displayedAnswer === option.value;
                    const isCorrect = Boolean(questionFeedback && option.value === questionFeedback.correctAnswer);
                    const isIncorrectSelection = Boolean(questionFeedback && isSelected && !questionFeedback.isCorrect);
                    return <Paper key={option.value} variant="outlined" sx={{ mb: 1.5, px: 2, py: 1, borderColor: isCorrect ? 'success.main' : isIncorrectSelection ? 'warning.main' : isSelected ? '#8900e1' : undefined, backgroundColor: isCorrect ? 'rgba(46,125,50,.08)' : isIncorrectSelection ? 'rgba(237,108,2,.08)' : undefined }}><FormControlLabel value={option.value} disabled={isQuestionLocked} control={<Radio sx={{ color: '#8900e1', '&.Mui-checked': { color: '#8900e1' } }} />} label={`${option.value === 'TRUE' || option.value === 'FALSE' ? '' : `${option.value}. `}${option.label}`} sx={{ m: 0, width: '100%' }} /></Paper>;
                  })}
                </RadioGroup>
              </FormControl>
              {questionFeedback ? <Alert severity={questionFeedback.severity}>{questionFeedback.isCorrect ? 'Correct.' : `Not quite. The correct answer is ${correctOption?.label || questionFeedback.correctAnswer}.`}</Alert> : null}
              {answerError ? <Alert severity="warning">{answerError}</Alert> : null}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}><Button variant="outlined" onClick={() => previousStatus && void handleStatusTransition(previousStatus)}>Back</Button><Button variant="contained" onClick={() => void (questionFeedback ? handleStatusTransition(questionFeedback.nextStatus) : handleQuestionSubmit())}>{questionFeedback ? 'Continue' : 'Submit Answer'}</Button></Box>
            </>
          ) : null}

          {currentStatus === 'Attestation' ? (
            <>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{AI_AWARENESS_TRAINING_COPY.attestation.title}</Typography>
              <Typography>{AI_AWARENESS_TRAINING_COPY.attestation.intro}</Typography>
              <Stack component="ul" spacing={1} sx={{ pl: 3, m: 0 }}>{AI_AWARENESS_TRAINING_COPY.attestation.bullets.map((bullet) => <Typography component="li" key={bullet}>{bullet}</Typography>)}</Stack>
              <Divider />
              <Box><Typography sx={{ fontWeight: 600, mb: 1.5 }}>Signature</Typography><Box sx={{ border: '1px solid', borderColor: attestationError ? 'error.main' : 'divider', borderRadius: 2, backgroundColor: '#fff', height: 180, overflow: 'hidden' }}><SignaturePad key={signatureRefreshKey} ref={signaturePadRef} onEnd={() => { if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) { const nextSignatureData = signaturePadRef.current.getCanvas().toDataURL('image/png'); setSignatureData(nextSignatureData); lastHydratedSignatureRef.current = nextSignatureData; if (isSignatureValid(signaturePadRef.current, nextSignatureData)) setAttestationError(''); } }} canvasProps={{ style: { width: '100%', height: '180px', display: 'block' } }} /></Box><Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}><Button variant="text" onClick={() => { signaturePadRef.current?.clear(); setSignatureData(''); setSignatureRefreshKey((value) => value + 1); lastHydratedSignatureRef.current = ''; }}>Clear Signature</Button></Box></Box>
              <FormControl error={Boolean(attestationError)}><FormControlLabel control={<Checkbox checked={isAttested} onChange={(event) => { setIsAttested(event.target.checked); setAttestationError(''); }} />} label={AI_AWARENESS_TRAINING_COPY.attestation.checkboxLabel} /></FormControl>
              {attestationError ? <Alert severity="warning">{attestationError}</Alert> : null}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Button variant="outlined" onClick={() => void handleStatusTransition('Bonus')}>Back</Button><Button variant="contained" onClick={() => void handleAttestationSubmit()}>Finish Training</Button></Box>
            </>
          ) : null}

          {currentStepIndex >= AI_AWARENESS_TRAINING_STATUSES.indexOf('Question 1') ? <><Divider /><Typography variant="body2" color="text.secondary">Current score: {session.correctAnswerCount} / {SCORED_QUESTION_COUNT} ({Math.round((session.score || 0) * 100)}%) · Bonus recorded separately</Typography></> : null}
        </Stack>
      </Paper>
    </Container>
  );
}
